import EmailIcon from '@mui/icons-material/Email'
import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import LocalPhoneIcon from '@mui/icons-material/LocalPhone'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    styled,
} from '@mui/material'
import { Stack } from '@mui/system'
import axios from 'axios'
import { useEffect, useRef } from 'react'
import { useAccount } from '../../context/account_context'
import { useBranch } from '../../context/branch_context'
import { PaymentType } from '../../context/buy_context'
import { ClientDocumentType } from '../../context/client_context'
import { CoinInfo } from '../../context/coin_context'
import { useConfigs } from '../../context/configs_context'
import { ProductInSale, SaleInfo } from '../../context/sale_context'
import { getFileEndpoint } from '../../services/media'
import {
    getDateInDDMMYYYYFormat,
    getDateString,
    getNoteNameCharLimit,
    maskCurrency,
    numeroPorExtensoEspanhol,
    quandoImagensCarregarem,
} from '../../utils/maks'
import { getCommonNoteSalePrintText } from '../../utils/print'

const StyledTableCell = styled(TableCell)(({}) => ({
    color: 'text.primary',
    border: 'none',
    padding: '1px 2px',
    fontSize: '11px',
}))

interface CommonNoteProps {
    sale: SaleInfo
    clientVia: boolean
    selectedCoin?: CoinInfo
}

const ContactIcon: Record<string, JSX.Element> = {
    Facebook: <FacebookIcon />,
    Instagram: <InstagramIcon />,
    Whatsapp: <WhatsAppIcon />,
    Endereço: <LocationOnIcon />,
    Telefone: <LocalPhoneIcon />,
    Email: <EmailIcon />,
}

function CommonNote({ sale, selectedCoin }: CommonNoteProps) {
    const { configs, contactTypes, base64Logo } = useConfigs()
    const { branches } = useBranch()

    const contacts = sale.branch ? sale.branch.contacts : configs.contacts
    const companyName =
        sale.branch && configs.showBranchNameInNote
            ? sale.branch.name
            : configs.companyName
    const companyLogo = sale.branch?.companyLogo
        ? branches.find((b) => b.id === sale.branch?.id)?.companyLogo?.content
        : base64Logo
    const companySlogan = sale.branch?.companySlogan
        ? sale.branch.companySlogan
        : configs.companySlogan

    const charNameLimit = getNoteNameCharLimit(configs)

    const totals =
        selectedCoin?.id !== sale.coin?.id
            ? sale.totalSecondaryCoin?.find(
                  (v) => v.coinId === selectedCoin?.id
              )!
            : sale

    const getJoinedSalesReport = () => {
        const prevSales: {
            id: number
            date: string
            products: ProductInSale[]
            sale?: SaleInfo
        }[] = []

        for (const product of sale.products) {
            const foundSale = prevSales.find((s) => s.id === product.prevSaleId)
            if (foundSale) {
                foundSale.products.push(product)
                continue
            }

            prevSales.push({
                id: product.prevSaleId as number,
                date: getDateString({
                    date: product.date ?? '',
                    hour: product.hour ?? '',
                    configs: configs,
                    isSummerTime: product.isSummerTime ?? false,
                }),
                products: [product],
                sale: product.prevSale,
            })
        }

        return (
            <Stack spacing={2}>
                {prevSales.map((prevSale) => (
                    <Stack borderBottom={'1px solid black'}>
                        <Typography p={'5px'} fontSize={'14px'}>
                            Número venta:{' '}
                            <Box component={'b'}>{prevSale.id}</Box>; Fecha:{' '}
                            {prevSale.date}; Valor pago:{' '}
                            <Box component={'b'}>
                                {maskCurrency(
                                    prevSale.sale?.totalAmoutPayed ?? 0,
                                    prevSale.sale?.coin
                                )}
                            </Box>
                        </Typography>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {configs.showImageOnNote && (
                                        <StyledTableCell
                                            sx={{
                                                borderBottom: '1px solid black',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            IMAGEN
                                        </StyledTableCell>
                                    )}
                                    {configs.showIdOnNote && (
                                        <StyledTableCell
                                            sx={{
                                                borderBottom: '1px solid black',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            CÓDIGO
                                        </StyledTableCell>
                                    )}
                                    {configs.showBarcodeOnNote && (
                                        <StyledTableCell
                                            sx={{
                                                borderBottom: '1px solid black',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            CÓDIGO BAR.
                                        </StyledTableCell>
                                    )}
                                    <StyledTableCell
                                        sx={{
                                            borderBottom: '1px solid black',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        CANT. / DESC.
                                    </StyledTableCell>
                                    {configs.showUnitPriceOnNote && (
                                        <StyledTableCell
                                            sx={{
                                                borderBottom: '1px solid black',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            P. UNITÁRIO
                                        </StyledTableCell>
                                    )}
                                    <StyledTableCell
                                        sx={{
                                            borderBottom: '1px solid black',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        P. VENTA
                                    </StyledTableCell>
                                    {configs.showWeightOnNote && (
                                        <StyledTableCell
                                            sx={{
                                                borderBottom: '1px solid black',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            PESO
                                        </StyledTableCell>
                                    )}
                                    <StyledTableCell
                                        sx={{
                                            borderBottom: '1px solid black',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        SUBTOTAL
                                    </StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {prevSale.products.map((product) => {
                                    const name = configs.showObservationOnNote
                                        ? product.product.parsedName
                                        : product.product.name

                                    return (
                                        <TableRow sx={{ height: '0' }}>
                                            {configs.showImageOnNote && (
                                                <StyledTableCell>
                                                    <Box
                                                        component={'img'}
                                                        src={
                                                            product.product
                                                                .image
                                                                ? getFileEndpoint(
                                                                      product
                                                                          .product
                                                                          .image
                                                                          ?.content
                                                                  )
                                                                : undefined
                                                        }
                                                        height={'50px'}
                                                        padding={'3px'}
                                                    />
                                                </StyledTableCell>
                                            )}
                                            {configs.showIdOnNote && (
                                                <StyledTableCell>
                                                    {
                                                        product.product
                                                            .internalCodes[0]
                                                    }
                                                </StyledTableCell>
                                            )}
                                            {configs.showBarcodeOnNote && (
                                                <StyledTableCell>
                                                    {product.product
                                                        .barcodes[0] || '---'}
                                                </StyledTableCell>
                                            )}
                                            <StyledTableCell>
                                                {product.qnt} /{' '}
                                                <Box
                                                    component={'span'}
                                                    fontSize={'9px'}
                                                    sx={{
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {name &&
                                                    name.length >
                                                        charNameLimit &&
                                                    !configs.ignoreCharacterLimitNote
                                                        ? name?.slice(
                                                              0,
                                                              charNameLimit
                                                          ) + '...'
                                                        : name}
                                                </Box>
                                            </StyledTableCell>
                                            {configs.showUnitPriceOnNote && (
                                                <StyledTableCell>
                                                    {maskCurrency(
                                                        product.unitPrice ?? 0,
                                                        prevSale.sale?.coin
                                                    )}
                                                </StyledTableCell>
                                            )}
                                            <StyledTableCell>
                                                {maskCurrency(
                                                    product.notePrice,
                                                    prevSale.sale?.coin
                                                )}
                                            </StyledTableCell>
                                            {configs.showWeightOnNote && (
                                                <StyledTableCell>
                                                    {product.product
                                                        .realWeight *
                                                        (product.qnt -
                                                            product.returnedQnt!)}{' '}
                                                    kg
                                                </StyledTableCell>
                                            )}
                                            <StyledTableCell>
                                                {maskCurrency(
                                                    product.subtotal,
                                                    prevSale.sale?.coin
                                                )}
                                            </StyledTableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                        <Typography
                            p={'5px'}
                            fontSize={'14px'}
                            textAlign={'right'}
                        >
                            Total:{' '}
                            <Box component={'b'}>
                                {maskCurrency(
                                    prevSale.products.reduce(
                                        (total, current) =>
                                            total + current.subtotal,
                                        0
                                    ),
                                    sale.coin
                                )}
                            </Box>
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        )
    }

    return (
        <Stack direction="column" width="100%" px="20px" py="10px">
            <Stack direction={'row'} width={'100%'}>
                <Stack
                    direction={'column'}
                    border={'1px solid black'}
                    borderRadius={'12px'}
                    width={'100%'}
                >
                    <Stack direction={'row'} p={'10px'}>
                        <Box
                            component={'img'}
                            src={companyLogo}
                            width={'220px'}
                            height={'110px'}
                            zIndex={'-1'}
                        />
                        <Stack textAlign={'center'} flex={'1'} px={'15px'}>
                            <Typography
                                fontSize={'14px'}
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {companyName}
                            </Typography>
                            <Typography
                                fontSize={'9px'}
                                sx={{ wordBreak: 'break-word' }}
                                fontStyle={'italic'}
                                whiteSpace={'pre-line'}
                            >
                                {`${companySlogan}`}
                            </Typography>
                            <Typography
                                fontSize={'8px'}
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {contactTypes
                                    .filter((ct) =>
                                        contacts.find(
                                            (c) => c.contactTypeId === ct.id
                                        )
                                    )
                                    .map(
                                        (ct) =>
                                            `${ct.name}: ${contacts
                                                .filter(
                                                    (c) =>
                                                        c.contactTypeId ===
                                                        ct.id
                                                )
                                                .map((c) => c.value)
                                                .join(', ')}`
                                    )
                                    .join('; ')}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
                <Stack
                    direction={'column'}
                    border={'1px solid black'}
                    borderRadius={'12px'}
                    width={'30%'}
                >
                    <Stack direction={'row'} p={'10px'}>
                        <Box flex={'1'}>
                            <Typography fontSize={'11px'}>
                                Cond. de venta:{' '}
                                {(sale.paymentTypeShow === PaymentType.Credit ||
                                    sale.paymentTypeShow ===
                                        PaymentType.Parcel) &&
                                !configs.showOnlyNormalPaymentType
                                    ? 'CRÉDITO'
                                    : 'CONTADO'}
                            </Typography>
                            <Typography fontSize={'11px'} mt={'1px'}>
                                Fecha:{' '}
                                {getDateString({
                                    date: sale.date,
                                    hour: sale.hour,
                                    configs: configs,
                                    isSummerTime: !!sale.isSummerTime,
                                })}
                            </Typography>
                            <Typography fontSize={'11px'} mt={'1px'}>
                                Vendedor: {sale.employee?.name}
                            </Typography>
                            <Typography fontSize={'11px'} mt={'1px'}>
                                Usr./Caja: {sale.account?.name}/
                                {sale.cashier?.name}
                            </Typography>
                            <Typography fontSize={'11px'} mt={'1px'}>
                                Fecha venc.:{' '}
                                {sale.expDate &&
                                sale.paymentType === PaymentType.Credit
                                    ? getDateInDDMMYYYYFormat(
                                          new Date(`${sale.expDate} 00:00:00`)
                                      )
                                    : '---'}
                            </Typography>
                            <Typography fontSize={'11px'} mt={'1px'}>
                                Orç.: {sale.preSaleId ?? '---'}
                            </Typography>
                            <Typography
                                fontSize={'13px'}
                                mt={'5px'}
                                fontWeight={'bold'}
                                textAlign={'center'}
                            >
                                Venta n.º {sale.id}
                            </Typography>
                        </Box>
                    </Stack>
                </Stack>
            </Stack>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderTop={'0'}
                borderRadius={'12px'}
                direction={'column'}
                padding={'5px'}
            >
                <Stack direction={'row'} alignItems={'center'}>
                    <Box width={'70%'}>
                        <Typography fontSize={'16px'} fontWeight={'bold'}>
                            Nombre: {sale.client?.name || '---'}
                        </Typography>
                        <Typography fontSize={'16px'} fontWeight={'bold'}>
                            {sale.client?.documentType ===
                            ClientDocumentType.RUC
                                ? 'Ruc'
                                : 'Documento'}
                            : {sale.client?.document || '---'}
                        </Typography>
                    </Box>
                    <Box>
                        <Box>
                            <Typography fontSize={'11px'}>
                                Telefonos:{' '}
                                {sale.client?.phoneNumbers.join(', ') || '---'}
                            </Typography>
                            <Typography fontSize={'11px'}>
                                Dirección: {sale.client?.address || '---'}
                            </Typography>
                        </Box>
                    </Box>
                </Stack>
            </Stack>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderTop={'0'}
                borderRadius={'12px'}
                direction={'column'}
                flex={'1'}
                justifyContent={'space-between'}
            >
                {sale.isJoinedNote ? (
                    getJoinedSalesReport()
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                {configs.showImageOnNote && (
                                    <StyledTableCell
                                        sx={{
                                            borderBottom: '1px solid black',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        IMAGEN
                                    </StyledTableCell>
                                )}
                                {configs.showIdOnNote && (
                                    <StyledTableCell
                                        sx={{
                                            borderBottom: '1px solid black',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        CÓDIGO
                                    </StyledTableCell>
                                )}
                                {configs.showBarcodeOnNote && (
                                    <StyledTableCell
                                        sx={{
                                            borderBottom: '1px solid black',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        CÓDIGO BAR.
                                    </StyledTableCell>
                                )}
                                <StyledTableCell
                                    sx={{
                                        borderBottom: '1px solid black',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    CANT. / DESC.
                                </StyledTableCell>
                                {configs.showUnitPriceOnNote && (
                                    <StyledTableCell
                                        sx={{
                                            borderBottom: '1px solid black',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        P. UNITÁRIO
                                    </StyledTableCell>
                                )}
                                <StyledTableCell
                                    sx={{
                                        borderBottom: '1px solid black',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    P. VENTA
                                </StyledTableCell>
                                {configs.showWeightOnNote && (
                                    <StyledTableCell
                                        sx={{
                                            borderBottom: '1px solid black',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        PESO
                                    </StyledTableCell>
                                )}
                                <StyledTableCell
                                    sx={{
                                        borderBottom: '1px solid black',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    SUBTOTAL
                                </StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sale.products.map((product) => {
                                const name = configs.showObservationOnNote
                                    ? product.product.parsedName
                                    : product.product.name

                                const values =
                                    selectedCoin?.id !== sale.coin?.id
                                        ? product.valueSecondaryCoin?.find(
                                              (v) =>
                                                  v.coinId === selectedCoin?.id
                                          )!
                                        : product

                                return (
                                    <TableRow sx={{ height: '0' }}>
                                        {configs.showImageOnNote && (
                                            <StyledTableCell>
                                                <Box
                                                    component={'img'}
                                                    src={
                                                        product.product.image
                                                            ? getFileEndpoint(
                                                                  product
                                                                      .product
                                                                      .image
                                                                      ?.content
                                                              )
                                                            : undefined
                                                    }
                                                    height={'50px'}
                                                    padding={'3px'}
                                                />
                                            </StyledTableCell>
                                        )}
                                        {configs.showIdOnNote && (
                                            <StyledTableCell>
                                                {
                                                    product.product
                                                        .internalCodes[0]
                                                }
                                            </StyledTableCell>
                                        )}
                                        {configs.showBarcodeOnNote && (
                                            <StyledTableCell>
                                                {product.product.barcodes[0] ||
                                                    '---'}
                                            </StyledTableCell>
                                        )}
                                        <StyledTableCell>
                                            {product.qnt} /{' '}
                                            <Box
                                                component={'span'}
                                                fontSize={'9px'}
                                                sx={{ wordBreak: 'break-word' }}
                                            >
                                                {name &&
                                                name.length > charNameLimit &&
                                                !configs.ignoreCharacterLimitNote
                                                    ? name.slice(
                                                          0,
                                                          charNameLimit
                                                      ) + '...'
                                                    : name}
                                            </Box>
                                        </StyledTableCell>
                                        {configs.showUnitPriceOnNote && (
                                            <StyledTableCell>
                                                {maskCurrency(
                                                    values?.unitPrice ?? 0,
                                                    selectedCoin
                                                )}
                                            </StyledTableCell>
                                        )}
                                        <StyledTableCell>
                                            {maskCurrency(
                                                values?.notePrice,
                                                selectedCoin
                                            )}
                                        </StyledTableCell>
                                        {configs.showWeightOnNote && (
                                            <StyledTableCell>
                                                {product.product.realWeight *
                                                    (product.qnt -
                                                        product.returnedQnt!)}{' '}
                                                kg
                                            </StyledTableCell>
                                        )}
                                        <StyledTableCell>
                                            {maskCurrency(
                                                values?.notePrice * product.qnt,
                                                selectedCoin
                                            )}
                                        </StyledTableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                )}
            </Stack>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderTop={'0'}
                borderRadius={'12px'}
                pl={'10px'}
                pb={'5px'}
            >
                <Stack
                    direction={'row'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                >
                    <Stack>
                        {!configs.hideWrittenTotal && (
                            <Typography fontSize={'12px'}>
                                Total a pagar:{' '}
                                <Box component={'span'} fontSize={'11px'}>
                                    (En letras)
                                </Box>{' '}
                                {numeroPorExtensoEspanhol(totals?.total, selectedCoin)}
                            </Typography>
                        )}
                        {/* {!configs.hideWrittenTotal && totalDiscount > 0 && (
                            <Typography fontSize={'12px'}>
                                Descuento:{' '}
                                {maskCurrency(totalDiscount, selectedCoin)}
                            </Typography>
                        )} */}
                        <Typography fontSize={'12px'}>
                            Nº de itens:{' '}
                            {sale.products.reduce(
                                (total, current) => total + current.qnt,
                                0
                            )}
                        </Typography>
                        {sale.observation && !sale.isJoinedNote && (
                            <Typography fontSize={'12px'}>
                                Nota: {sale.observation}
                            </Typography>
                        )}
                    </Stack>

                    <Typography
                        fontSize={'12px'}
                        borderLeft={'1px solid black'}
                        borderBottom={'1px solid black'}
                        p={'5px'}
                    >
                        {maskCurrency(totals?.total, selectedCoin)}
                    </Typography>
                </Stack>
            </Stack>
            {!!configs.observationCommonNote && (
                <Stack
                    width={'100%'}
                    border={'1px solid black'}
                    borderTop={'0'}
                    borderRadius={'12px'}
                    pl={'10px'}
                    pb={'5px'}
                >
                    <Typography fontSize={'12px'}>
                        {configs.observationCommonNote}
                    </Typography>
                </Stack>
            )}
        </Stack>
    )
}

interface Props {
    sales: SaleInfo[]
    handleClose(): void
    selectedCoin?: CoinInfo
    dontPrint?: boolean
    isMatrix?: boolean
}

export function CommonNoteDialog({
    handleClose,
    sales,
    selectedCoin,
    dontPrint,
    isMatrix,
}: Props) {
    const { configs } = useConfigs()
    const { accountInfo } = useAccount()
    const printRef = useRef<HTMLElement>()

    useEffect(() => {
        if (!isMatrix) {
            if (!printRef.current) return

            if (!dontPrint) {
                printRef.current!.style.visibility = 'hidden'
                printRef.current!.style.position = 'fixed'
            }

            quandoImagensCarregarem(printRef.current).then(() => {
                const twoPages = !configs.printHalfPage || dontPrint

                for (let child in printRef.current!.children) {
                    if (isNaN(Number(child))) continue
                    const element = printRef.current!.children[
                        child
                    ] as HTMLElement

                    const a4HeightPx = 1122
                    let heightInVh = (element.offsetHeight / a4HeightPx) * 100

                    if (heightInVh < 50 && !twoPages) {
                        heightInVh = 50
                    } else {
                        heightInVh =
                            100 * (Math.floor((heightInVh - 1) / 100) + 1)
                    }

                    element.style.height = `${heightInVh}vh`
                }

                if (!dontPrint) {
                    setTimeout(() => {
                        printRef.current!.style.position = 'absolute'
                        printRef.current!.className = isMatrix
                            ? 'printableMatrix'
                            : 'printable'
                        window.scroll(0, 0)
                        window.print()
                        handleClose()
                    }, 100)
                }
            })
        } else {
            let finalContent = ''

            for (const sale of sales) {
                while (sale.products.length) {
                    const { content, productsUsed } =
                        getCommonNoteSalePrintText(
                            { ...sale },
                            configs,
                            selectedCoin
                        )

                    sale.products.splice(0, productsUsed)

                    console.log(content)
                    console.log(
                        `Número de linhas: ${content.split('\n').length}`
                    )

                    finalContent += content
                }
            }

            const baseHost =
                accountInfo?.localPrinterHost ?? 'http://localhost:3500'

            axios
                .post(`${baseHost}/imprimir`, {
                    conteudo: finalContent,
                    impressora: 'ImpressoraMatricial',
                })
                .catch((err) => console.log(err))
                .finally(() => handleClose())
        }
    }, [])

    if (isMatrix) return <></>

    return (
        <Box id="note" ref={printRef}>
            {sales.map((sale) => (
                <>
                    {Array.from({
                        length: !dontPrint ? configs.notesQnt : 1,
                    }).map(() => (
                        <CommonNote
                            sale={sale}
                            clientVia={true}
                            selectedCoin={selectedCoin ?? sale.coin}
                        />
                    ))}
                </>
            ))}
        </Box>
    )
}
